import React, { useState, useEffect } from 'react';
import { Form, Input, Button, DatePicker, Row, Col, Upload, message } from 'antd';
import { WCFDA } from '../../types/wordpress';
import { UploadOutlined } from '@ant-design/icons';
import type { RcFile, UploadFile } from 'antd/es/upload/interface';
import dayjs from 'dayjs';
import { WordPressAPI } from '../../services/api/api';

interface FDAFormProps {
    initialValues?: WCFDA | null;
    onSubmit: (values: Partial<WCFDA>) => void;
    onCancel: () => void;
}

const FDAForm: React.FC<FDAFormProps> = ({ initialValues, onSubmit, onCancel }) => {
    const [form] = Form.useForm();
    const [attachments, setAttachments] = useState<any[]>(initialValues?.attachments || []);

    const normFile = (e: any) => {
        if (Array.isArray(e)) {
            return e;
        }
        return e?.fileList;
    };

    const beforeUpload = async (file: RcFile): Promise<boolean> => {
        try {
            const postId = initialValues?.id;
            const response = await WordPressAPI.uploadFDAFile(file, postId);

            if (response?.data) {
                const newAttachment = {
                    uid: file.uid,
                    name: response.data.name,
                    status: 'done',
                    url: response.data.url,
                    type: response.data.type
                };

                setAttachments(prev => [...prev, newAttachment]);
                form.setFieldValue('attachments', [...attachments, newAttachment]);
            }

            return false;
        } catch (error) {
            console.error('Upload error:', error);
            message.error('Failed to upload file');
            return false;
        }
    };

    useEffect(() => {
        form.setFieldValue('attachments', attachments);
    }, [attachments]);

    return (
        <Form
            form={form}
            layout="vertical"
            initialValues={{
                ...initialValues,
                expiryDate: initialValues?.expiry_date ? dayjs(initialValues.expiry_date) : undefined,
                attachments: initialValues?.attachments?.map(file => ({
                    uid: file.name,
                    name: file.name,
                    status: 'done',
                    url: file.url,
                })) as UploadFile[],
            }}
            onFinish={onSubmit}
        >
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item
                        name="name"
                        label="Name"
                        rules={[{ required: true, message: 'Please enter name' }]}
                    >
                        <Input />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        name="telephone"
                        label="Telephone"
                        rules={[{ required: true, message: 'Please enter telephone number' }]}
                    >
                        <Input />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={24}>
                    <Form.Item
                        name="address"
                        label="Address"
                        rules={[{ required: true, message: 'Please enter address' }]}
                    >
                        <Input.TextArea rows={3} />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item
                        name="registration_number"
                        label="Registration Number"
                    >
                        <Input />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        name="expiry_date"
                        label="Expiry Date"
                    >
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={24}>
                    <Form.Item
                        name="attachments"
                        label="Attachments"
                        valuePropName="fileList"
                        getValueFromEvent={normFile}
                        rules={[{ required: true, message: 'Please upload at least one file' }]}
                    >
                        <Upload
                            beforeUpload={beforeUpload}
                            multiple
                            listType="text"
                        >
                            <Button icon={<UploadOutlined />}>Click to Upload</Button>
                        </Upload>
                    </Form.Item>
                </Col>
            </Row>

            <Form.Item>
                <div style={{ textAlign: 'right' }}>
                    <Button onClick={onCancel} style={{ marginRight: 8 }}>
                        Cancel
                    </Button>
                    <Button type="primary" htmlType="submit">
                        {initialValues ? 'Update' : 'Create'} Registration
                    </Button>
                </div>
            </Form.Item>
        </Form>
    );
};

export default FDAForm; 